import { useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { saveCertificate } from '../../firebase/certificateUtils';
import { storage } from '../../firebase/config';
import firebaseUtils from '../../utils/firebase-utils-simple';
import '../../styles/components/certificates/PdfGenerator.css';

const formatDisplayDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

let cachedLogoDataUrl = '';
export const fetchLogoDataUrl = async () => {
  if (cachedLogoDataUrl) return cachedLogoDataUrl;
  try {
    const response = await fetch(`${process.env.PUBLIC_URL || ''}/logos/SHZ.png`, { cache: 'force-cache' });
    if (!response.ok) return '';
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('image')) return '';
    const blob = await response.blob();
    const reader = new FileReader();
    const dataUrlPromise = new Promise((resolve, reject) => {
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          resolve('');
        }
      };
      reader.onerror = reject;
    });
    reader.readAsDataURL(blob);
    const dataUrl = await dataUrlPromise;
    cachedLogoDataUrl = dataUrl;
    return dataUrl;
  } catch (err) {
    console.error('No se pudo cargar el logo para el PDF', err);
    return '';
  }
};

export const buildCertificatePdf = (data, logoDataUrl = '') => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 46;

  const green = [35, 142, 71];
  const gray = [66, 66, 66];

  doc.setFont('helvetica', 'normal');
  doc.setLineHeightFactor(1.25);

  // Header (izquierda texto, derecha logo con sello "- Original -")
  doc.setTextColor(...green);
  doc.setFontSize(14);
  doc.text('Moving big things.', margin, 50);
  doc.text('Safely.', margin, 68);

  if (logoDataUrl) {
    try {
      const logoWidth = 96;
      const logoHeight = 64;
      const logoX = pageWidth - margin - logoWidth;
      const logoY = 32;
      doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
      doc.setTextColor(gray[0], gray[1], gray[2]);
      doc.setFontSize(10);
      doc.text('- Original -', logoX + logoWidth / 2, logoY + logoHeight + 16, { align: 'center' });
    } catch (err) {
      console.warn('No se pudo insertar el logo en el PDF, se continua sin logo.', err);
      doc.setTextColor(...green);
    }
  }

  // Título centrado
  doc.setTextColor(...green);
  doc.setFontSize(18);
  doc.text('Declaración de Conformidad EC', pageWidth / 2, 150, { align: 'center' });
  doc.setFontSize(11);
  doc.text('según la directiva de máquinas 2006/42/EU', pageWidth / 2, 168, { align: 'center' });

  // Bloque "Nosotros" alineado con datos de compañía a la derecha
  doc.setTextColor(...gray);
  doc.setFontSize(11);
  const companyX = margin + 220;
  doc.text('Nosotros,', margin, 205);
  doc.setFont('helvetica', 'normal');
  doc.text('SHZ Sächsische Hebe- und Zurrtechnik GmbH', companyX, 205);
  doc.text('Arnsdorfer Weg 5', companyX, 220);
  doc.text('01900 Großröhrsdorf', companyX, 235);

  // Párrafo principal
  const paragraph1 = 'La máquina/producto que se menciona a continuación cumple, tanto en su concepción y construcción como en su realización, todos los requisitos esenciales y relevantes de seguridad y salud exigidos por la norma CE de máquinas.';
  const paragraph2 = 'Esta declaración CE de conformidad perderá su validez como consecuencia de una modificación o adición no autorizada por nosotros. Asimismo, esta declaración CE de conformidad perderá su validez si la máquina/producto no se utiliza en las aplicaciones previstas descritas en las instrucciones de funcionamiento o si no se realizan las inspecciones periódicas requeridas.';
  const wrapWidth = pageWidth - margin * 2;
  const p1Lines = doc.splitTextToSize(paragraph1, wrapWidth);
  const p2Lines = doc.splitTextToSize(paragraph2, wrapWidth);
  let y = 270;
  doc.text(p1Lines, margin, y, { lineHeightFactor: 1.3 });
  const p1Box = doc.getTextDimensions(p1Lines, { fontSize: 11 });
  y += p1Box.h + 12;
  doc.text(p2Lines, margin, y, { lineHeightFactor: 1.3 });
  const p2Box = doc.getTextDimensions(p2Lines, { fontSize: 11 });
  y += p2Box.h + 28;

  // Datos técnicos
  y = Math.max(y, 360);
  const labelX = margin;
  const valueX = margin + 190;
  const addField = (label, value) => {
    doc.setFont('helvetica', 'normal');
    doc.text(`${label}:`, labelX, y);
    doc.setFont('helvetica', 'bold');
    doc.text(value || '-', valueX, y);
    y += 20;
  };

  addField('Número de Ítem', data.numeroItem || data.numeroItemProducto);
  addField('Tipo de Máquina/Producto', data.tipoMaquina || data.tipoMaquinaProducto);
  addField('Elevación de Largo', data.largoEfectivo);
  addField('Color', data.color);
  addField('WLL', data.capacidadWLL || data.capacidad);

  y += 12;
  addField('Código Único', data.codigoUnico);
  addField('Norma Aplicada', data.normaAplicada);

  doc.setFont('helvetica', 'normal');
  doc.text('Normas o especificaciones técnicas aplicadas total o parcialmente:', labelX, y + 10);
  const normas = (data.normaAplicada || '').split(/\n|;/).map((n) => n.trim()).filter(Boolean);
  const normasList = normas.length ? normas : ['-'];
  normasList.forEach((norma, idx) => {
    doc.setFont('helvetica', 'bold');
    doc.text(norma, valueX, y + 10 + idx * 14);
  });

  y += normasList.length ? (10 + normasList.length * 14 + 16) : 42;
  addField('Nota de entrega', data.notaEntrega);
  addField('Fecha', formatDisplayDate(data.fecha || data.fechaInspeccion));

  // Footer
  const footerStart = Math.max(y + 36, pageHeight - 220);
  doc.setFont('helvetica', 'normal');
  doc.text('La persona autorizada para la elaboración de documentos técnicos es:', margin, footerStart);
  doc.setFont('helvetica', 'bold');
  doc.text('SHZ Sächsische Hebe- und Zurrtechnik GmbH', margin, footerStart + 18);
  doc.text('Arnsdorfer Weg 5, 01900 Großröhrsdorf', margin, footerStart + 34);

  if (logoDataUrl) {
    try {
      const watermarkWidth = 180;
      const watermarkHeight = 60;
      const watermarkX = (pageWidth - watermarkWidth) / 2;
      const watermarkY = footerStart + 48;
      doc.addImage(logoDataUrl, 'PNG', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
    } catch (err) {
      console.warn('No se pudo insertar la marca de agua en el PDF.', err);
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.text('Großröhrsdorf, ' + formatDisplayDate(data.fecha || new Date()), margin, footerStart + 120);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...gray);
  const contactY = pageHeight - 54;
  doc.text('SHZ Sächsische Hebe- und Zurrtechnik GmbH', margin, contactY);
  doc.text('Arnsdorfer Weg 5, 01900 Großröhrsdorf', margin, contactY + 12);
  doc.text('Tel.: +49 3595 33939 0', pageWidth - margin, contactY, { align: 'right' });
  doc.text('Fax: +49 3595 33939 10', pageWidth - margin, contactY + 12, { align: 'right' });
  doc.text('info@shz.de', pageWidth / 2, contactY + 12, { align: 'center' });
  doc.text('www.shz.de', pageWidth / 2, contactY + 24, { align: 'center' });
  doc.setFontSize(11);
  doc.setTextColor(...gray);

  return doc.output('blob');
};

export const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

function PdfGenerator({ user }) {
  const [mode, setMode] = useState('datos'); // 'datos' | 'upload'
  const [status, setStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  const [detailForm, setDetailForm] = useState({
    usuarioId: '',
    usuarioEmail: '',
    nombre: '',
    empresa: user?.empresa || '',
    tipo: 'Certificación',
    numeroItem: '',
    tipoMaquina: '',
    largoEfectivo: '',
    color: '',
    capacidad: '',
    factorSeguridad: '',
    codigoUnico: '',
    notaEntrega: '',
    normaAplicada: '',
    fecha: ''
  });

  const [uploadForm, setUploadForm] = useState({
    nombre: '',
    fecha: '',
    usuarioId: '',
    usuarioEmail: '',
    empresa: user?.empresa || ''
  });
  const [uploadFile, setUploadFile] = useState(null);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const storageEnabled = Boolean(process.env.REACT_APP_STORAGE_PROVIDER !== 'none');

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        setUsersError('');
        const data = await firebaseUtils.getUsuarios();
        setUsers(data || []);
      } catch (err) {
        setUsersError('No se pudieron cargar usuarios');
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const loadLogo = async () => {
      const dataUrl = await fetchLogoDataUrl();
      if (dataUrl) setLogoDataUrl(dataUrl);
    };

    loadLogo();
  }, []);

  useEffect(() => {
    if (!users.length) return;
    const match = users.find((u) => u.uid === user?.uid || u.email === user?.email);
    if (match) {
      setDetailForm((prev) => ({ ...prev, usuarioId: match.id || match.uid, usuarioEmail: match.email || '' }));
      setUploadForm((prev) => ({ ...prev, usuarioId: match.id || match.uid, usuarioEmail: match.email || '' }));
    }
  }, [users, user]);

  if (!user) {
    return <p>Debes iniciar sesión para generar certificados.</p>;
  }

  const uploadFileToStorage = async (file, ownerId) => {
    const safeOwner = ownerId || 'unknown';
    const path = `certificados/${safeOwner}/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, path);
    try {
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type || 'application/pdf'
      });
      return getDownloadURL(snapshot.ref);
    } catch (err) {
      console.error('Error subiendo PDF a Storage', err);
      throw err;
    }
  };

  const handleDetailChange = (event) => {
    const { name, value } = event.target;
    setDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailUserSelect = (event) => {
    const selectedId = event.target.value;
    const selectedUser = users.find((u) => (u.id || u.uid) === selectedId);
    setDetailForm((prev) => ({
      ...prev,
      usuarioId: selectedId,
      usuarioEmail: selectedUser?.email || ''
    }));
  };

  const handleUploadChange = (event) => {
    const { name, value } = event.target;
    setUploadForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUploadUserSelect = (event) => {
    const selectedId = event.target.value;
    const selectedUser = users.find((u) => (u.id || u.uid) === selectedId);
    setUploadForm((prev) => ({
      ...prev,
      usuarioId: selectedId,
      usuarioEmail: selectedUser?.email || ''
    }));
  };

  const handleSubmitDetail = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: '' });
    setSubmitting(true);

    if (!detailForm.usuarioId) {
      setStatus({ type: 'error', message: 'Debes seleccionar un usuario registrado.' });
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...detailForm,
        userId: detailForm.usuarioId,
        usuarioEmail: detailForm.usuarioEmail || user?.email || '',
        pdfUrl: '',
        tipo: detailForm.tipo || 'Certificación',
        codigoUnico: detailForm.codigoUnico,
        numeroItem: detailForm.numeroItem,
        tipoMaquinaProducto: detailForm.tipoMaquina,
        largoEfectivo: detailForm.largoEfectivo,
        color: detailForm.color,
        capacidadWLL: detailForm.capacidad,
        factorSeguridad: detailForm.factorSeguridad,
        notaEntrega: detailForm.notaEntrega,
        normaAplicada: detailForm.normaAplicada,
        origen: 'generado'
      };

      if (!payload.fecha) {
        payload.fecha = new Date().toISOString();
      }

      let logoToUse = logoDataUrl;
      if (!logoToUse) {
        logoToUse = await fetchLogoDataUrl();
        if (logoToUse) setLogoDataUrl(logoToUse);
      }

      const pdfBlob = buildCertificatePdf(payload, logoToUse);
      const filename = `${payload.codigoUnico || 'certificado'}-${Date.now()}.pdf`;

      let pdfUrl = '';
      if (storageEnabled) {
        try {
          const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });
          pdfUrl = await uploadFileToStorage(pdfFile, detailForm.usuarioId);
        } catch (uploadErr) {
          console.warn('No se pudo subir a Storage, se descarga localmente.', uploadErr);
        }
      }

      if (!pdfUrl) {
        triggerDownload(pdfBlob, filename);
      }

      payload.pdfUrl = pdfUrl;

      const id = await saveCertificate(payload);
      const msg = pdfUrl ? `Certificado y PDF generados (ID: ${id}).` : 'Certificado guardado. PDF descargado localmente.';
      setStatus({ type: 'success', message: msg });
      setDetailForm({
        usuarioId: detailForm.usuarioId,
        usuarioEmail: detailForm.usuarioEmail || user?.email || '',
        nombre: '',
        empresa: user?.empresa || '',
        tipo: 'Certificación',
        numeroItem: '',
        tipoMaquina: '',
        largoEfectivo: '',
        color: '',
        capacidad: '',
        factorSeguridad: '',
        codigoUnico: '',
        notaEntrega: '',
        normaAplicada: '',
        fecha: ''
      });
    } catch (error) {
      console.error('Error al guardar certificado:', error);
      const hint = error?.code === 'storage/unknown' || error?.message?.includes('Precondition')
        ? 'No se pudo subir el PDF. Verifica bucket de Firebase Storage, reglas y que el usuario esté autenticado.'
        : 'No se pudo guardar. Revisa credenciales/permisos.';
      setStatus({ type: 'error', message: hint });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitUpload = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: '' });

    if (!uploadForm.usuarioId) {
      setStatus({ type: 'error', message: 'Debes seleccionar un usuario registrado.' });
      return;
    }

    if (!uploadFile) {
      setStatus({ type: 'error', message: 'Selecciona un PDF para subir.' });
      return;
    }

    if (!storageEnabled) {
      setStatus({ type: 'error', message: 'Subir PDF está deshabilitado (no hay Storage configurado).' });
      return;
    }

    setSubmitting(true);
    try {
      const pdfUrl = await uploadFileToStorage(uploadFile, uploadForm.usuarioId);
      const payload = {
        userId: uploadForm.usuarioId,
        usuarioEmail: uploadForm.usuarioEmail || user?.email || '',
        nombre: uploadForm.nombre,
        empresa: uploadForm.empresa,
        fecha: uploadForm.fecha || new Date().toISOString(),
        pdfUrl,
        tipo: 'Adjunto',
        origen: 'upload',
        estado: 'activo'
      };

      const id = await saveCertificate(payload);
      setStatus({ type: 'success', message: `PDF subido y asociado (ID: ${id}).` });
      setUploadForm({
        usuarioId: uploadForm.usuarioId,
        usuarioEmail: uploadForm.usuarioEmail || user?.email || '',
        nombre: '',
        fecha: '',
        empresa: user?.empresa || ''
      });
      setUploadFile(null);
    } catch (error) {
      console.error('Error al subir PDF:', error);
      setStatus({ type: 'error', message: 'No se pudo subir el PDF. Revisa permisos/conexión.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="certificate-generator">
      <div className="generator-header">
        <div>
          <p className="eyebrow">Certificados</p>
          <h2>Generar o adjuntar PDF</h2>
          <p className="subtitle">Completa los datos clave o sube un PDF directamente para asociarlo al cliente.</p>
        </div>
        <div className="mode-switch">
          <button
            type="button"
            className={mode === 'datos' ? 'mode-btn active' : 'mode-btn'}
            onClick={() => setMode('datos')}
          >
            Generar con datos
          </button>
          <button
            type="button"
            className={mode === 'upload' ? 'mode-btn active' : 'mode-btn'}
            onClick={() => setMode('upload')}
          >
            Subir PDF
          </button>
        </div>
      </div>

      {usersError && <p className="status-error">{usersError}</p>}

      {mode === 'datos' && (
        <form onSubmit={handleSubmitDetail} className="certificate-form grid">
          <div className="form-row">
            <label>Usuario registrado
              <select value={detailForm.usuarioId} onChange={handleDetailUserSelect} required disabled={loadingUsers}>
                <option value="" disabled>{loadingUsers ? 'Cargando usuarios...' : 'Selecciona un usuario'}</option>
                {users.map((u) => (
                  <option key={u.id || u.uid} value={u.id || u.uid}>{u.email || u.nombre || u.id}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>Nombre del certificado
              <input name="nombre" value={detailForm.nombre} onChange={handleDetailChange} required placeholder="Ej: Certificado de Calidad" />
            </label>
            <label>Empresa
              <input name="empresa" value={detailForm.empresa} onChange={handleDetailChange} placeholder="Empresa" />
            </label>
          </div>

          <div className="form-row">
            <label>Tipo Máquina / Producto
              <input name="tipoMaquina" value={detailForm.tipoMaquina} onChange={handleDetailChange} placeholder="Ej: Puente grúa" />
            </label>
            <label>Número de Item
              <input name="numeroItem" value={detailForm.numeroItem} onChange={handleDetailChange} placeholder="Ej: 12345" />
            </label>
          </div>

          <div className="form-row">
            <label>Largo Efectivo
              <input name="largoEfectivo" value={detailForm.largoEfectivo} onChange={handleDetailChange} placeholder="Ej: 12 m" />
            </label>
            <label>Color
              <input name="color" value={detailForm.color} onChange={handleDetailChange} placeholder="Ej: Verde" />
            </label>
          </div>

          <div className="form-row">
            <label>Capacidad de carga (WLL)
              <input name="capacidad" value={detailForm.capacidad} onChange={handleDetailChange} placeholder="Ej: 5 ton" />
            </label>
            <label>Factor de Seguridad
              <input name="factorSeguridad" value={detailForm.factorSeguridad} onChange={handleDetailChange} placeholder="Ej: 6:1" />
            </label>
          </div>

          <div className="form-row">
            <label>Código Único
              <input name="codigoUnico" value={detailForm.codigoUnico} onChange={handleDetailChange} required placeholder="Ej: AMS-2026-001" />
            </label>
            <label>Fecha
              <input type="date" name="fecha" value={detailForm.fecha} onChange={handleDetailChange} />
            </label>
          </div>

          <div className="form-row">
            <label>Nota de entrega
              <input name="notaEntrega" value={detailForm.notaEntrega} onChange={handleDetailChange} placeholder="Ej: Entregado en planta" />
            </label>
            <label>Norma aplicada
              <input name="normaAplicada" value={detailForm.normaAplicada} onChange={handleDetailChange} placeholder="Ej: ASME B30" />
            </label>
          </div>

          <div className="form-row">
            <label>Tipo
              <input name="tipo" value={detailForm.tipo} onChange={handleDetailChange} placeholder="Certificación" />
            </label>
          </div>

          <div className="actions">
            <button type="submit" disabled={submitting}>{submitting ? 'Guardando...' : 'Guardar certificado'}</button>
          </div>
        </form>
      )}

      {mode === 'upload' && (
        <form onSubmit={handleSubmitUpload} className="certificate-form upload">
          <div className="form-row">
            <label>Nombre del certificado
              <input name="nombre" value={uploadForm.nombre} onChange={handleUploadChange} required placeholder="Ej: Certificado inspección" />
            </label>
            <label>Fecha
              <input type="date" name="fecha" value={uploadForm.fecha} onChange={handleUploadChange} />
            </label>
          </div>

          <div className="form-row">
            <label>Usuario registrado
              <select value={uploadForm.usuarioId} onChange={handleUploadUserSelect} required disabled={loadingUsers}>
                <option value="" disabled>{loadingUsers ? 'Cargando usuarios...' : 'Selecciona un usuario'}</option>
                {users.map((u) => (
                  <option key={u.id || u.uid} value={u.id || u.uid}>{u.email || u.nombre || u.id}</option>
                ))}
              </select>
            </label>
            <label>Email seleccionado
              <input name="usuarioEmail" value={uploadForm.usuarioEmail} onChange={handleUploadChange} readOnly placeholder="Se completa al elegir usuario" />
            </label>
            <label>Empresa
              <input name="empresa" value={uploadForm.empresa} onChange={handleUploadChange} placeholder="Empresa" />
            </label>
          </div>

          <div className="form-row">
            <label>Seleccionar PDF
              <input type="file" accept="application/pdf" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} required />
            </label>
          </div>

          <div className="actions">
            <button type="submit" disabled={submitting}>{submitting ? 'Subiendo...' : 'Subir y asociar'}</button>
          </div>
        </form>
      )}

      {status.type && (
        <p className={status.type === 'error' ? 'status-error' : 'status-success'}>
          {status.message}
        </p>
      )}
    </div>
  );
}

export default PdfGenerator;
